import React, { useState, useRef, useEffect, memo } from 'react'
import PropTypes from 'prop-types'
import { TextField, Paper, IconButton, Popper, ClickAwayListener } from '@mui/material'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import styles from './AriaDatePicker.module.scss'

const formatDateForDisplay = (date) => {
  if (!date) return ''

  /* istanbul ignore next -- component always receives Date objects */
  if (!(date instanceof Date)) {
    date = new Date(date)
  }

  if (isNaN(date.getTime())) {
    console.warn('Invalid date passed to formatDateForDisplay:', date)
    return ''
  }

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

const createNormalizedDate = (year, month, day) => {

  const date = new Date(year, month, day, 12, 0, 0)

  return date
}

const isSameDay = (date1, date2) => {
  if (!date1 || !date2) return false

  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  )
}

const AriaDatePicker = memo(function AriaDatePicker (props) {
  const { label, value, onChange, disabled = false, minDate, maxDate } = props

  const inputRef = useRef(null)
  const [anchorEl, setAnchorEl] = useState(null)
  const [open, setOpen] = useState(false)

  const today = new Date()
  const [viewDate, setViewDate] = useState(() => {
    return value instanceof Date ? new Date(value) : new Date()
  })

  /* eslint-disable react-hooks/set-state-in-effect -- sync viewDate when external value prop changes */
  useEffect(() => {
    if (value instanceof Date) {
      setViewDate(new Date(value))
    }
  }, [value])
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleInputClick = () => {
    if (!disabled) {
      setAnchorEl(inputRef.current)
      setOpen(true)
    }
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handlePrevMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1))
  }

  const handleNextMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1))
  }

  const isDateDisabled = (date) => {
    if (minDate) {
      const min = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate())
      const check = new Date(date.getFullYear(), date.getMonth(), date.getDate())
      if (check < min) return true
    }
    if (maxDate) {
      const max = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate())
      const check = new Date(date.getFullYear(), date.getMonth(), date.getDate())
      if (check > max) return true
    }
    return false
  }

  const handleDateSelect = (year, month, day) => {
    const selectedDate = createNormalizedDate(year, month, day)
    if (isDateDisabled(selectedDate)) return

    if (onChange) {
      onChange(selectedDate)
    }
    handleClose()
  }

  const getMonthName = (date) => {
    return date.toLocaleString('default', { month: 'long' })
  }

  const generateCalendarGrid = () => {
    const year = viewDate.getFullYear()
    const month = viewDate.getMonth()

    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)

    const firstDayOfWeek = firstDay.getDay()
    const daysInMonth = lastDay.getDate()

    const weekdays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

    const days = []

    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push(null)
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day)
    }

    return (
      <div className={styles.grid}>
        {/* Weekday headers */}
        {weekdays.map((weekday) => (
          <div key={weekday} className={styles.weekday}>
            {weekday}
          </div>
        ))}

        {/* Calendar days */}
        {days.map((day, index) => {
          if (day === null) {
            return <div key={`empty-${index}`} />
          }

          const date = createNormalizedDate(year, month, day)
          const isSelected = value && isSameDay(date, value)
          const isToday = isSameDay(date, today)
          const isDisabled = isDateDisabled(date)

          return (
            <div
              key={`day-${day}`}
              className={`${styles.cell} ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''} ${isDisabled ? 'disabled' : ''}`}
              onClick={() => !isDisabled && handleDateSelect(year, month, day)}
              aria-disabled={isDisabled}
            >
              {day}
            </div>
          )
        })}
      </div>
    )
  }

  const displayValue = formatDateForDisplay(value)

  return (
    <div className={styles.inputContainer}>
      <TextField
        label={label}
        value={displayValue}
        placeholder='YYYY-MM-DD'
        className={styles.textField}
        variant='outlined'
        inputRef={inputRef}
        onClick={handleInputClick}
        disabled={disabled}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <IconButton
              className={styles.calendarButton}
              onClick={handleInputClick}
              size='small'
              disabled={disabled}
              aria-label='Open calendar'
            >
              <CalendarTodayIcon />
            </IconButton>
          )
        }}
        InputLabelProps={{
          shrink: true,
          style: { fontWeight: 600 }
        }}
      />

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement='bottom-start'
        className={styles.datePicker}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper className={styles.popover}>
            <div className={styles.calendar}>
              <div className={styles.header}>
                <IconButton
                  className={styles.navButton}
                  size='small'
                  onClick={handlePrevMonth}
                >
                  <ChevronLeftIcon fontSize='small' />
                </IconButton>
                <div className={styles.title}>
                  {getMonthName(viewDate)} {viewDate.getFullYear()}
                </div>
                <IconButton
                  className={styles.navButton}
                  size='small'
                  onClick={handleNextMonth}
                >
                  <ChevronRightIcon fontSize='small' />
                </IconButton>
              </div>

              {generateCalendarGrid()}
            </div>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </div>
  )
})

AriaDatePicker.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.instanceOf(Date),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date)
}

export default AriaDatePicker
